// src/components/forms/common.tsx
//
// The parts every create/edit form repeats: how a server rejection is shown, how a
// destructive action is confirmed, and how money crosses the wire. Money is stored
// in minor units everywhere; forms take major units because that is what people type.

import { useState, type ReactNode } from 'react';
import { ApiRequestError } from '../../lib/api';
import { Button, Modal, Textarea } from '../ui';

/**
 * Server errors, readably. A zod failure arrives as { fieldErrors, formErrors } in
 * `details`, and showing which field was rejected saves the guessing game.
 */
export function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  const err = error as ApiRequestError;
  const details = (err?.details ?? null) as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | null;
  const fields = Object.entries(details?.fieldErrors ?? {}).slice(0, 4);
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">
      <p className="font-medium">{err?.message ?? 'Something went wrong'}</p>
      {(details?.formErrors ?? []).map((f) => <p key={f} className="mt-0.5">{f}</p>)}
      {fields.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {fields.map(([field, messages]) => <li key={field}><span className="font-medium">{field}</span>: {messages.join(', ')}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Two-column form grid. Pass `wide` on a child to make it span both columns. */
export const FormGrid = ({ children }: { children: ReactNode }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
export const Wide = ({ children }: { children: ReactNode }) => <div className="sm:col-span-2">{children}</div>;

/**
 * Confirmation for anything that removes or ends something. `reasonLabel` turns it
 * into a reason-required dialog, which is what the API wants for the serious ones.
 */
export function Confirm({ open, onClose, title, body, confirmLabel = 'Confirm', danger = true, pending, error, reasonLabel, onConfirm }: {
  open: boolean;
  onClose: () => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  error?: unknown;
  reasonLabel?: string;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const blocked = !!reasonLabel && !reason.trim();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant={danger ? 'danger' : 'primary'} disabled={pending || blocked} onClick={() => onConfirm(reason.trim())}>{pending ? 'Working…' : confirmLabel}</Button>
      </>}
    >
      <div className="space-y-4">
        <div className="text-[13.5px] leading-relaxed text-ink-600">{body}</div>
        {reasonLabel && (
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-ink-600">{reasonLabel}</span>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" placeholder="Recorded on the audit trail" />
          </label>
        )}
        <FormError error={error} />
      </div>
    </Modal>
  );
}

// ── Money ────────────────────────────────────────────────────────────────────
/** "1,850.50" → 185050. Blank means "not given", never zero. */
export const toMinor = (major: string | number | null | undefined): number | undefined => {
  if (major === null || major === undefined || String(major).trim() === '') return undefined;
  const n = Number(String(major).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
};

/** 185050 → "1850.50", for putting a stored amount back into an input. */
export const toMajor = (minor: number | null | undefined): string =>
  minor === null || minor === undefined ? '' : (minor / 100).toFixed(2).replace(/\.00$/, '');

/** Blank string → undefined, so a PATCH never clears a field the form did not touch. */
export const trimmed = (v: string | null | undefined): string | undefined => {
  const s = (v ?? '').trim();
  return s === '' ? undefined : s;
};

/** Blank string → null, for the fields the API accepts as "explicitly empty". */
export const nulled = (v: string | null | undefined): string | null => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

export const numberOr = (v: string | number | null | undefined, fallback?: number): number | undefined => {
  if (v === null || v === undefined || String(v).trim() === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** "Gym, Bike store, Roof terrace" → ['Gym','Bike store','Roof terrace'] */
export const csvToList = (v: string): string[] => v.split(',').map((s) => s.trim()).filter(Boolean);
